import { useState, useEffect } from "react";
import { Box, Drawer, useTheme } from "@mui/material";
import { authService } from "@/services/auth.service";
import { useAuthStore } from "@/store/authStore";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { ToastContainer } from "../ui/ToastContainer";

const DRAWER_WIDTH = 256; // w-64

interface MainLayoutProps {
  children: React.ReactNode;
}

/**
 * Layout principal de l'application avec Sidebar et TopBar
 * Basé sur la maquette défi_gourmand_admin_dashboard_1
 */
export function MainLayout({ children }: MainLayoutProps): JSX.Element {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  // Restaurer l'utilisateur au rechargement (token en localStorage mais user non persisté)
  useEffect(() => {
    if (token && !user) {
      authService.getMe().then(setUser).catch(() => {});
    }
  }, [token, user, setUser]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* Sidebar Desktop */}
      <Box
        component="nav"
        sx={{
          width: { md: DRAWER_WIDTH },
          flexShrink: { md: 0 },
          display: { xs: "none", md: "block" },
          height: "100vh",
        }}
      >
        <Sidebar />
      </Box>

      {/* Sidebar Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
          },
        }}
      >
        <Sidebar />
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
          bgcolor: "background.default",
        }}
      >
        <TopBar onMenuClick={handleDrawerToggle} />

        {/* Content Scroll Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            p: { xs: 2, md: 3 },
            scrollBehavior: "smooth",
            "&::-webkit-scrollbar": {
              width: 6,
              height: 6,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "light" ? "#e5e7eb" : "#404040",
              borderRadius: 3,
            },
          }}
        >
          <Box sx={{ maxWidth: 1280, mx: "auto", width: "100%" }}>
            {children}
          </Box>
        </Box>
      </Box>

      {/* Toast Container pour les notifications */}
      <ToastContainer />
    </Box>
  );
}
